/* ==========================================================================
   Module accent system — single source of truth
   --------------------------------------------------------------------------
   Imported by both the navigation grid (pages/dashboard-pages/NavigationPage)
   and the application sidebar (components/MainSidebar) so a module's tile,
   icon and hover colour are identical on both surfaces by construction.

   Previously each module carried its own hue (emerald, violet, indigo, red,
   orange, cyan, teal, amber, purple) and the two screens defined them
   separately, so they drifted apart. The palette is now three tiers of the
   brand blue, assigned by what the module does:

     primary   #0084CF  analytics and live insight
     secondary #0066A3  day-to-day operations
     deep      #1C4D88  data, supply chain, records and governance

   Class strings are written out in full so Tailwind's scanner can see them.
   ========================================================================== */

export type AccentTier = 'primary' | 'secondary' | 'deep';

export interface Accent {
  /** Raw hex, for inline styles, SVG props and chart marks. */
  hex: string;
  /** Icon tile: tinted at rest, brand-filled on group hover. */
  tile: string;
  /** Icon glyph colour, inverting on group hover. */
  icon: string;
  /** Label colour on group hover. */
  text: string;
}

export const ACCENT: Record<AccentTier, Accent> = {
  primary: {
    hex: '#0084CF',
    tile: 'bg-[#F2F8FC] dark:bg-[#162238] border-[#C2DFF1] dark:border-[#20324F] group-hover:bg-[#0084CF] group-hover:border-[#0084CF] hover:bg-[#0084CF] hover:border-[#0084CF]',
    icon: 'text-[#0084CF] dark:text-[#38BDF8] group-hover:text-white hover:text-white',
    text: 'group-hover:text-[#0084CF] dark:group-hover:text-[#38BDF8]',
  },
  secondary: {
    hex: '#0084CF',
    tile: 'bg-[#F2F8FC] dark:bg-[#162238] border-[#C2DFF1] dark:border-[#20324F] group-hover:bg-[#0084CF] group-hover:border-[#0084CF] hover:bg-[#0084CF] hover:border-[#0084CF]',
    icon: 'text-[#0084CF] dark:text-[#38BDF8] group-hover:text-white hover:text-white',
    text: 'group-hover:text-[#0084CF] dark:group-hover:text-[#38BDF8]',
  },
  deep: {
    hex: '#0084CF',
    tile: 'bg-[#F2F8FC] dark:bg-[#162238] border-[#C2DFF1] dark:border-[#20324F] group-hover:bg-[#0084CF] group-hover:border-[#0084CF] hover:bg-[#0084CF] hover:border-[#0084CF]',
    icon: 'text-[#0084CF] dark:text-[#38BDF8] group-hover:text-white hover:text-white',
    text: 'group-hover:text-[#0084CF] dark:group-hover:text-[#38BDF8]',
  },
};

/** Which tier each module belongs to. Unlisted keys fall back to `primary`. */
export const MODULE_TIER: Record<string, AccentTier> = {
  home: 'deep',
  dashboard: 'primary',
  generation_reports: 'primary',
  live_dashboards: 'primary',
  site_information: 'secondary',
  om: 'secondary',
  alerts: 'secondary',
  simulation: 'secondary',
  dsm: 'secondary',
  upload: 'deep',
  inventory: 'deep',
  vendors: 'deep',
  documentation: 'deep',
  compliance: 'deep',
};

export const asTier = (value: string): AccentTier =>
  (value in ACCENT ? value : MODULE_TIER[value] || 'primary') as AccentTier;

export const accentFor = (value: string): Accent => ACCENT[asTier(value)];

export const accentHex = (value: string): string => accentFor(value).hex;
